import { makeObservable, observable, computed, action } from 'mobx';
import { InputBaseModel } from '../models';
import { InputChangeType, InputChangeRecord } from '../types';
import { InputChangeTracker } from '.';

type ChangeCounts = Record<InputChangeType, { count: number; inputIds: string[] }>;

export type AggregatedChanges = {
    counts: ChangeCounts;
    changeRecords: InputChangeRecord[];
    totalChangeCount: number;
};

export default class InputGroupChangeTracker {
    inputChangeTrackers: Map<string, InputChangeTracker> = new Map();

    constructor() {
        makeObservable(this, {
            inputChangeTrackers: observable,
            createInputChangeTracker: action,
            clearInputChangeTrackers: action,
            aggregatedChanges: computed
        });
    }

    static aggregateChanges(changeRecords: InputChangeRecord[]): AggregatedChanges {
        const counts: ChangeCounts = {
            added: { count: 0, inputIds: [] },
            removed: { count: 0, inputIds: [] },
            modified: { count: 0, inputIds: [] },
            unsaved: { count: 0, inputIds: [] }
        };

        changeRecords.forEach(record => {
            counts[record.changeType].count++;
            counts[record.changeType].inputIds.push(record.inputId);
        });

        return { counts, changeRecords, totalChangeCount: changeRecords.length };
    }

    get aggregatedChanges(): AggregatedChanges {
        return InputGroupChangeTracker.aggregateChanges(
            Array.from(this.inputChangeTrackers.values()).flatMap(tracker =>
                tracker.changeType
                    ? [
                          {
                              inputId: tracker.inputId,
                              inputName: tracker.inputName,
                              newInput: tracker.newInput,
                              previousInput: tracker.previousInput,
                              changeType: tracker.changeType
                          }
                      ]
                    : []
            )
        );
    }

    createInputChangeTracker(
        inputId: string,
        newInput?: InputBaseModel,
        previousInput?: InputBaseModel
    ): InputChangeTracker {
        this.inputChangeTrackers.get(inputId)?.dispose();

        const inputChangeTracker = new InputChangeTracker(inputId, newInput, previousInput);
        this.inputChangeTrackers.set(inputId, inputChangeTracker);
        return inputChangeTracker;
    }

    getInputChangeTracker(inputId: string): InputChangeTracker | undefined {
        return this.inputChangeTrackers.get(inputId);
    }

    clearInputChangeTrackers() {
        this.inputChangeTrackers.forEach(tracker => tracker.dispose());
        this.inputChangeTrackers.clear();
    }
}
